import React, { useEffect, useState } from "react";
import BreadCrumbs from "../../../components/BreadCrumbs";
import { Card, CardBody, CardHeader, Col } from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import ViewButtonComponent from "../../../components/ViewButtonComponent";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setRowsPerPage, setSelectedCart } from "../../../redux/cartSlice";
import TableLoadingComponent from "../../../components/TableLoadingComponent";
import ControlledSearch from "../../../components/ControlledSearch";
import { useGetCartSearchResultsMutation } from "../../../redux/services/orderService";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useFirebaseCartsByRestaurantId } from "../../../hooks/useCartDetail";

export interface CartList {
  createdAt: any;
  displayName: string;
  id: string;
  imageUrl: string;
  isFixedLocation: boolean;
  cartState: string;
  location: any;
  merchantId: string;
  owner: {
    email: string;
    id: string;
    phoneNumber: string;
    photoUrl?: string;
    userId: string;
    displayName?: string;
  };
  restaurantIds: string[];
  updatedAt?: any;
  liveLocation?: any;
  status?: string;
  priority?: string;
  assignedGate?: string;
  cartType?: string;
}

const CartListTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filteredCarts, setFilteredCarts] = useState<CartList[]>([]);
  const [searchKey, setSearchKey] = useState(0);
  const [cartOptions, setCartOptions] = useState<any>([]);
  const [cartLoading, setCartLoading] = useState(false);
  
  const { control, setValue } = useForm();
  const [triggerCartSearchList] = useGetCartSearchResultsMutation();

  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.id
  );

  const rowsPerPage = useSelector((state: any) => state.cart.rowsPerPage);

  const formatCartDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    
    try {
      let date: Date | null = null;
      
      if (timestamp && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      else if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
        date = new Date(timestamp.seconds * 1000);
      }
      else if (timestamp && typeof timestamp === 'object' && '_seconds' in timestamp) {
        date = new Date(timestamp._seconds * 1000);
      }
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      
      if (date && !isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
          timeZone: 'Asia/Colombo', 
          timeZoneName: 'short' 
        }).replace('GMT', '').trim(); 
      }
    } catch (error) {
    }
    
    return 'Invalid Date';
  };

  const { 
    data: cartsData, 
    loading: isCartsLoading, 
    error 
  } = useFirebaseCartsByRestaurantId(selectedRestaurantId);

  useEffect(() => {
    if (cartsData?.carts) {
      setFilteredCarts(cartsData.carts);
    }
  }, [cartsData]);

  // Handle error
  useEffect(() => {
    if (error) {
      toast.error("Error loading carts: " + error.message);
    }
  }, [error]);

  const handleView = (cart: CartList) => {
    // Save cart to Redux before navigating
    dispatch(setSelectedCart(cart));
    
    navigate("/carts/cart-list/view", {
      state: {
        carts: cart,
        activeTab: 1,
        selectedDate: new Date().toISOString().split("T")[0]
      },
      replace: false
    });
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setRowsPerPage(newRowsPerPage));
  };

  const columns: TableColumn<CartList>[] = [
    {
      name: <h6 className="fw-bold">Name</h6>,
      selector: (row) => row.displayName,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      selector: (row) => formatCartDate(row.createdAt),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Updated Date</h6>,
      selector: (row) => row.updatedAt ? formatCartDate(row.updatedAt) : "N/A",
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Cart State</h6>,
      selector: (row) => row.cartState,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Action</h6>,
      cell: (row) => (
        <div className="d-flex gap-2">
          <ViewButtonComponent onClick={() => handleView(row)} />
        </div>
      ),
    },
  ];

  const handleCartSearch = async (inputValue: string) => {
    setCartLoading(true);

    if (inputValue.length > 1) {
      try {
        const response = await triggerCartSearchList({ params: `query=${inputValue}` });

        if (response && "data" in response) {
          const optionsList = response.data?.hits?.map((item: any) => ({
            value: item.cartId,
            label: item.displayName,
          }));
          setCartOptions(optionsList || []);
        }
      } catch (error) {
        toast.error("Error fetching cart results");
      }
    }
    setCartLoading(false);
  };

  const handleCartInputChange = (inputValue: string) => {
    if (inputValue.length > 2) {
      handleCartSearch(inputValue);
    }
  };

  const handleCartSelect = (selectedOption: any) => {
    setValue("cart", selectedOption);

    if (!selectedOption) {
      setFilteredCarts(cartsData?.carts || []);
      return;
    }

    const filtered = (cartsData?.carts || []).filter(
      (c) =>
        c.id === selectedOption.value ||
        c.displayName === selectedOption.label ||
        c.owner?.userId === selectedOption.value
    );

    setFilteredCarts(filtered.length ? filtered : []);
  };

  // Reset search when restaurant changes
  useEffect(() => {
    setSearchKey(prev => prev + 1);
    setValue("cart", null);
  }, [selectedRestaurantId, setValue]);

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <Card className="mt-3">
        <CardHeader className="bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <Col>
              <h4 className="mb-0">Cart List</h4>
              {cartsData?.carts && (
                <small className="text-muted">
                  Total Carts: {cartsData.carts.length}
                </small>
              )}
            </Col>
            <Col lg="3">
              <ControlledSearch
                key={`user-cart-search-${searchKey}`}
                inputName={"cart"}
                control={control}
                optionsList={cartOptions}
                loading={cartLoading}
                handleInputChange={handleCartInputChange}
                onChange={handleCartSelect}
                placeholder={"Search Cart Name..."}
              />
            </Col>
          </div>
        </CardHeader>
        <CardBody>
          {isCartsLoading ? (
            <TableLoadingComponent numColumns={6} />
          ) : (
            <>
              <DataTable
                columns={columns}
                data={filteredCarts}
                responsive
                pagination
                highlightOnHover
                paginationPerPage={rowsPerPage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                noDataComponent={
                  <div className="text-center p-4">
                    No carts found for this restaurant
                  </div>
                }
              />
            </>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default CartListTable;